import React, { useState, useEffect } from 'react';
import { FaTimes, FaBullhorn, FaInfoCircle, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { BANNER_CONFIG, BANNER_ADMIN_CONFIG, BANNER_DRIVER_CONFIG, BANNER_CUSTOMER_CONFIG } from '../../../backend';
import './AnnouncementBanner.css';

const AnnouncementBanner = ({ role = 'global',}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    // Create a map to access the correct banner config
    const roleConfigs = {
      global: BANNER_CONFIG,
      admin: BANNER_ADMIN_CONFIG,
      driver: BANNER_DRIVER_CONFIG,
      customer: BANNER_CUSTOMER_CONFIG,
    };

    // fallback to global config if role-specific not found
    const getRoleConfig = () => {
      const selectedConfig = roleConfigs[role] || BANNER_CONFIG;
      return {
        isEnabled: selectedConfig.enabled.toString().toLowerCase()==="true" ?? BANNER_CONFIG.enabled.toString().toLowerCase()==="true",
        message: selectedConfig.message ?? BANNER_CONFIG.message,
        type: selectedConfig.type ?? BANNER_CONFIG.type,
        startDate: selectedConfig.startDate ?? BANNER_CONFIG.startDate,
        endDate: selectedConfig.endDate ?? BANNER_CONFIG.endDate,
      };
    };

    const config = getRoleConfig();
    const { isEnabled, message, type, startDate, endDate } = config;

    useEffect(() => {
        // Check if banner is enabled
        if (!isEnabled || !message) {
            setIsVisible(false);
            return;
        }

        // Check if user has dismissed the banner in current session
        const dismissedBanners = JSON.parse(sessionStorage.getItem('dismissedBanners') || '{}');
        const bannerKey = `${role}-${message}-${startDate}-${endDate}`;
        
        if (dismissedBanners[bannerKey]) {
            setIsVisible(false);
            return;
        }

        // Check date range
        const now = new Date();
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        const isWithinDateRange = 
            (!start || now >= start) && 
            (!end || now <= end);

        if (isWithinDateRange) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [message, startDate, endDate, isEnabled, role]);

    const handleDismiss = () => {
        const bannerKey = `${role}-${message}-${startDate}-${endDate}`;
        const dismissedBanners = JSON.parse(sessionStorage.getItem('dismissedBanners') || '{}');
        dismissedBanners[bannerKey] = true;
        sessionStorage.setItem('dismissedBanners', JSON.stringify(dismissedBanners));
        
        setIsDismissed(true);
        setTimeout(() => setIsVisible(false), 300);
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <FaCheckCircle />;
            case 'warning':
                return <FaExclamationTriangle />;
            case 'announcement':
                return <FaBullhorn />;
            case 'info':
            default:
                return <FaInfoCircle />;
        }
    };

    if (!isVisible) return null;

    return (
        <div className={`announcement-banner announcement-banner-${type} ${isDismissed ? 'dismissed' : ''}`}>
            <div className="announcement-banner-content">
                <div className="announcement-banner-icon">
                    {getIcon()}
                </div>
                <div className="announcement-banner-message">
                    {message}
                </div>
                <button 
                    className="announcement-banner-close"
                    onClick={handleDismiss}
                    aria-label="Dismiss announcement"
                >
                    <FaTimes />
                </button>
            </div>
        </div>
    );
};

export default AnnouncementBanner;